import React from "react";
import "./Markdown.css";

// Minimal, dependency-free Markdown renderer for Ana's chat replies.
// Safe by construction: it only ever renders strings as text — it never
// touches innerHTML — so streamed model output cannot inject markup.
// Supported subset: ## headings, - / 1. lists, blank-line paragraphs, and
// inline **bold**, *italic* and `code`.

const HEADING = /^(#{1,6})\s+(.*)$/;
const UNORDERED = /^\s*[-*]\s+(.*)$/;
const ORDERED = /^\s*\d+\.\s+(.*)$/;
const INLINE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;

function renderInline(text) {
  return String(text)
    .split(INLINE)
    .filter((part) => part !== "")
    .map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("`") && part.endsWith("`")) {
        return <code key={i}>{part.slice(1, -1)}</code>;
      }
      if (part.startsWith("*") && part.endsWith("*")) {
        return <em key={i}>{part.slice(1, -1)}</em>;
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
}

function renderBlocks(text) {
  const blocks = [];
  let paragraph = [];
  let list = null;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    blocks.push(
      <p key={blocks.length} className="trunfo-md__p">
        {renderInline(paragraph.join(" "))}
      </p>
    );
    paragraph = [];
  };

  const flushList = () => {
    if (!list) return;
    const items = list.items.map((item, i) => <li key={i}>{renderInline(item)}</li>);
    const Tag = list.ordered ? "ol" : "ul";
    blocks.push(
      <Tag key={blocks.length} className="trunfo-md__list">
        {items}
      </Tag>
    );
    list = null;
  };

  const pushHeading = (match) => {
    const Tag = `h${Math.min(match[1].length + 2, 6)}`;
    blocks.push(
      <Tag key={blocks.length} className="trunfo-md__h">
        {renderInline(match[2])}
      </Tag>
    );
  };

  const pushListItem = (content, ordered) => {
    if (!list || list.ordered !== ordered) {
      flushList();
      list = { ordered, items: [] };
    }
    list.items.push(content);
  };

  for (const line of String(text).replace(/\r\n/g, "\n").split("\n")) {
    const heading = line.match(HEADING);
    const ordered = line.match(ORDERED);
    const unordered = line.match(UNORDERED);

    if (line.trim() === "") {
      flushParagraph();
      flushList();
    } else if (heading) {
      flushParagraph();
      flushList();
      pushHeading(heading);
    } else if (ordered) {
      flushParagraph();
      pushListItem(ordered[1], true);
    } else if (unordered) {
      flushParagraph();
      pushListItem(unordered[1], false);
    } else {
      flushList();
      paragraph.push(line.trim());
    }
  }

  flushParagraph();
  flushList();
  return blocks;
}

export default function Markdown({ content }) {
  return <div className="trunfo-md">{renderBlocks(content)}</div>;
}
